import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Link } from 'react-router-dom';
import { 
  Heart, 
  Award, 
  Users, 
  Sparkles, 
  Shield, 
  Clock,
  MessageCircle,
  Phone,
  CheckCircle,
  Star
} from 'lucide-react';

const About = () => {
  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-soft overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="space-y-4">
                <h1 className="text-hero text-foreground">About Glow by Precious</h1>
                <p className="text-xl text-primary font-medium">Your Glow, Your Power</p>
                <p className="text-lg text-muted-foreground leading-relaxed">
                  Redefining beauty, health, and wellness in Polokwane through personalized care, 
                  advanced treatments, and a commitment to helping you discover your inner radiance.
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <a href="https://wa.me/27695523598" target="_blank" rel="noopener noreferrer">
                  <Button className="gradient-primary text-white shadow-elegant hover:shadow-lg transition-all duration-300 w-full sm:w-auto">
                    <MessageCircle className="h-4 w-4 mr-2" />
                    Book Consultation
                  </Button>
                </a>
                <Link to="/services">
                  <Button variant="outline" className="border-primary text-primary hover:bg-primary hover:text-white transition-smooth w-full sm:w-auto">
                    View Our Services
                  </Button>
                </Link>
              </div>
            </div>
            
            <div className="relative">
              <img 
                src="./images/diverse_women_wellness_2.jpeg" 
                alt="Diverse women celebrating wellness and natural beauty"
                className="w-full h-96 object-cover rounded-2xl shadow-elegant"
              />
              <div className="absolute -top-6 -right-6 w-24 h-24 gradient-primary rounded-full flex items-center justify-center shadow-elegant animate-float">
                <Heart className="h-8 w-8 text-white" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center space-y-4 mb-16">
              <h2 className="text-section text-foreground">Our Story</h2>
              <p className="text-lg text-muted-foreground">
                Founded on the belief that everyone deserves to feel confident and beautiful
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
              <div className="space-y-6">
                <p className="text-muted-foreground leading-relaxed">
                  Glow by Precious was born from a passion for helping people discover their natural beauty 
                  and achieve optimal wellness. Located in the vibrant city of Polokwane, we recognized 
                  the need for a premium wellness destination that combines advanced technology with 
                  personalized, compassionate care.
                </p>
                
                <p className="text-muted-foreground leading-relaxed">
                  Our founder, Precious, envisioned a space where clients could embark on transformative 
                  wellness journeys in a safe, professional, and welcoming environment. Today, we continue 
                  to uphold that vision by offering cutting-edge treatments that deliver real, lasting results.
                </p>
                
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 text-primary" />
                    <span className="text-foreground">Professional, qualified practitioners</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 text-primary" />
                    <span className="text-foreground">State-of-the-art equipment and technology</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 text-primary" />
                    <span className="text-foreground">Personalized treatment plans</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 text-primary" />
                    <span className="text-foreground">Commitment to safety and hygiene</span>
                  </div>
                </div>
              </div>
              
              <div className="relative">
                <img 
                  src="./images/hero_spa_interior_2.jpeg" 
                  alt="Elegant spa interior with relaxing atmosphere"
                  className="w-full h-80 object-cover rounded-2xl shadow-elegant"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Values Section */}
      <section className="py-20 bg-gradient-soft">
        <div className="container mx-auto px-4">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-section text-foreground">Our Core Values</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              These principles guide everything we do and shape the experience we create for every client
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <Card className="shadow-soft hover:shadow-elegant transition-all duration-300 border-0 bg-gradient-to-br from-white to-pink-50">
              <CardContent className="p-8 text-center space-y-4">
                <div className="w-16 h-16 gradient-primary rounded-full flex items-center justify-center mx-auto shadow-elegant">
                  <Heart className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-foreground">Compassionate Care</h3>
                <p className="text-muted-foreground leading-relaxed">
                  We treat every client with empathy, respect, and genuine care, creating a supportive 
                  environment for your wellness journey.
                </p>
              </CardContent>
            </Card>
            
            <Card className="shadow-soft hover:shadow-elegant transition-all duration-300 border-0 bg-gradient-to-br from-white to-pink-50">
              <CardContent className="p-8 text-center space-y-4">
                <div className="w-16 h-16 gradient-primary rounded-full flex items-center justify-center mx-auto shadow-elegant">
                  <Award className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-foreground">Excellence</h3>
                <p className="text-muted-foreground leading-relaxed">
                  We strive for excellence in every treatment, using the best equipment and techniques 
                  to deliver outstanding results.
                </p>
              </CardContent>
            </Card>
            
            <Card className="shadow-soft hover:shadow-elegant transition-all duration-300 border-0 bg-gradient-to-br from-white to-pink-50">
              <CardContent className="p-8 text-center space-y-4">
                <div className="w-16 h-16 gradient-primary rounded-full flex items-center justify-center mx-auto shadow-elegant">
                  <Shield className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-foreground">Safety & Trust</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Your safety is our top priority. We maintain the highest standards of hygiene 
                  and professional practice.
                </p>
              </CardContent>
            </Card>
            
            <Card className="shadow-soft hover:shadow-elegant transition-all duration-300 border-0 bg-gradient-to-br from-white to-pink-50">
              <CardContent className="p-8 text-center space-y-4">
                <div className="w-16 h-16 gradient-primary rounded-full flex items-center justify-center mx-auto shadow-elegant">
                  <Sparkles className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-foreground">Innovation</h3>
                <p className="text-muted-foreground leading-relaxed">
                  We continuously invest in the latest technology and techniques to provide 
                  cutting-edge wellness solutions.
                </p>
              </CardContent>
            </Card>
            
            <Card className="shadow-soft hover:shadow-elegant transition-all duration-300 border-0 bg-gradient-to-br from-white to-pink-50">
              <CardContent className="p-8 text-center space-y-4">
                <div className="w-16 h-16 gradient-primary rounded-full flex items-center justify-center mx-auto shadow-elegant">
                  <Users className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-foreground">Inclusivity</h3>
                <p className="text-muted-foreground leading-relaxed">
                  We welcome and celebrate diversity, creating an inclusive space where everyone 
                  feels valued and respected.
                </p>
              </CardContent>
            </Card>
            
            <Card className="shadow-soft hover:shadow-elegant transition-all duration-300 border-0 bg-gradient-to-br from-white to-pink-50">
              <CardContent className="p-8 text-center space-y-4">
                <div className="w-16 h-16 gradient-primary rounded-full flex items-center justify-center mx-auto shadow-elegant">
                  <Clock className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-foreground">Commitment</h3>
                <p className="text-muted-foreground leading-relaxed">
                  We're committed to your long-term wellness goals and will support you 
                  throughout your transformation journey.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center space-y-4 mb-16">
              <h2 className="text-section text-foreground">Why Clients Choose Glow by Precious</h2>
              <p className="text-lg text-muted-foreground">
                Discover what sets us apart in Polokwane's wellness landscape
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
              <div className="space-y-8">
                <div className="flex gap-4">
                  <div className="w-12 h-12 gradient-primary rounded-full flex items-center justify-center flex-shrink-0">
                    <Award className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-foreground mb-2">Professional Expertise</h3>
                    <p className="text-muted-foreground">
                      Our team consists of qualified professionals with extensive experience in beauty, 
                      health, and wellness treatments.
                    </p>
                  </div>
                </div>
                
                <div className="flex gap-4">
                  <div className="w-12 h-12 gradient-primary rounded-full flex items-center justify-center flex-shrink-0">
                    <Sparkles className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-foreground mb-2">Advanced Technology</h3>
                    <p className="text-muted-foreground">
                      We use state-of-the-art equipment including RF, cavitation, laser, and vacuum 
                      bipolar technology for optimal results.
                    </p>
                  </div>
                </div>
                
                <div className="flex gap-4">
                  <div className="w-12 h-12 gradient-primary rounded-full flex items-center justify-center flex-shrink-0">
                    <Heart className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-foreground mb-2">Personalized Approach</h3>
                    <p className="text-muted-foreground">
                      Every treatment plan is customized to your unique needs, goals, and lifestyle 
                      for the best possible outcomes.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="space-y-8">
                <div className="flex gap-4">
                  <div className="w-12 h-12 gradient-primary rounded-full flex items-center justify-center flex-shrink-0">
                    <Shield className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-foreground mb-2">Safety Standards</h3>
                    <p className="text-muted-foreground">
                      We maintain strict hygiene protocols and safety standards to ensure your 
                      complete peace of mind during treatments.
                    </p>
                  </div>
                </div>
                
                <div className="flex gap-4">
                  <div className="w-12 h-12 gradient-primary rounded-full flex items-center justify-center flex-shrink-0">
                    <Users className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-foreground mb-2">Proven Results</h3>
                    <p className="text-muted-foreground">
                      Join hundreds of satisfied clients who have achieved their wellness goals 
                      through our comprehensive treatment programs.
                    </p>
                  </div>
                </div>
                
                <div className="flex gap-4">
                  <div className="w-12 h-12 gradient-primary rounded-full flex items-center justify-center flex-shrink-0">
                    <Clock className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-foreground mb-2">Convenient Location</h3>
                    <p className="text-muted-foreground">
                      Located in central Polokwane with secured parking and flexible hours 
                      to accommodate your busy schedule.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-gradient-soft">
        <div className="container mx-auto px-4">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-section text-foreground">What Our Clients Say</h2>
            <p className="text-lg text-muted-foreground">
              Real stories from real people who have experienced transformation at Glow by Precious
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="shadow-soft hover:shadow-elegant transition-all duration-300 border-0">
              <CardContent className="p-6 space-y-4">
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 text-yellow-500 fill-current" />
                  ))}
                </div>
                <p className="text-muted-foreground italic">
                  "The body contouring treatments at Glow by Precious have been life-changing. 
                  The staff is professional, caring, and the results speak for themselves!"
                </p>
                <div className="pt-4 border-t border-border">
                  <p className="font-semibold text-foreground">Sarah M.</p>
                  <p className="text-sm text-muted-foreground">Body Contouring Client</p>
                </div>
              </CardContent>
            </Card>
            
            <Card className="shadow-soft hover:shadow-elegant transition-all duration-300 border-0">
              <CardContent className="p-6 space-y-4">
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 text-yellow-500 fill-current" />
                  ))}
                </div>
                <p className="text-muted-foreground italic">
                  "I love the IV drip therapy sessions! I feel more energized and my skin has 
                  never looked better. Highly recommend Glow by Precious."
                </p>
                <div className="pt-4 border-t border-border">
                  <p className="font-semibold text-foreground">Thabo K.</p>
                  <p className="text-sm text-muted-foreground">IV Therapy Client</p>
                </div>
              </CardContent>
            </Card>
            
            <Card className="shadow-soft hover:shadow-elegant transition-all duration-300 border-0">
              <CardContent className="p-6 space-y-4">
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 text-yellow-500 fill-current" />
                  ))}
                </div>
                <p className="text-muted-foreground italic">
                  "The massage therapy here is incredible. It's my monthly treat to myself, 
                  and I always leave feeling completely rejuvenated."
                </p>
                <div className="pt-4 border-t border-border">
                  <p className="font-semibold text-foreground">Nomsa L.</p>
                  <p className="text-sm text-muted-foreground">Massage Therapy Client</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 gradient-primary text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-pink-600/20 to-purple-600/20"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center space-y-8">
            <div className="space-y-4">
              <h2 className="text-section text-white">Ready to Start Your Wellness Journey?</h2>
              <p className="text-xl text-pink-100 max-w-2xl mx-auto">
                Join our community of satisfied clients and discover what Glow by Precious can do for you.
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <a href="https://wa.me/27695523598" target="_blank" rel="noopener noreferrer">
                <Button size="lg" className="bg-white text-primary hover:bg-pink-50 shadow-elegant hover:shadow-lg transition-all duration-300 text-lg px-8 py-4 w-full sm:w-auto">
                  <MessageCircle className="h-5 w-5 mr-2" />
                  Book Your Consultation
                </Button>
              </a>
              <a href="tel:0695523598">
                <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-primary transition-smooth text-lg px-8 py-4 w-full sm:w-auto">
                  <Phone className="h-5 w-5 mr-2" />
                  Call: 069 552 3598
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default About;